import { Box } from '@mui/material';
import { useAppSelector } from 'store/hook';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { isDarkTheme } from 'utils/theme';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import DtcHandler from 'handlers/dtc.handler';
import DriverBehaviorHandler from 'handlers/driver.behavior.handler';
import FuelAnalyticsHandler from 'handlers/fuelAnalytics.handler';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { ProductionWidget } from './components/ProductionWidget';
import { UtilizationWidget } from './components/UtilizationWidget';
import { useEffect } from 'react';
import { MachineHealthWidget } from './components/MachineHealthWidget';
import { OperatorBehavior } from './components/OperatorBehaviour';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { formatStartEndDateUTC } from 'utils/helpers/dateTime';
import dayjs from 'dayjs';
import { generateAssetRecommendations } from './components/utils';

const PlanningAid = () => {
  const { customerCode } = useCustomerInfo();
  const squaresHandler = new SquaresDashboardHandler();
  const dtcHandler = new DtcHandler();
  const driverBehaviorHandler = new DriverBehaviorHandler();
  const fuelAnalyticsHandler = new FuelAnalyticsHandler();
  const currentDate = new Date();

  const prevDate = new Date();
  prevDate.setDate(prevDate.getDate() - 1);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const { data, loadingStates } = useAppSelector(
    (state) => state.productionReducer
  );
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const utilizationReducer = useAppSelector(
    (state) => state.utilizationReducer
  );
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const { downTimeData } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );
  const notifications = useAppSelector(
    (state) => state.tireProfileReducer.dashboard?.notifications
  );
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  const { isLoading: isLoadingDtc, allGroupedDTCData } = useAppSelector(
    (state) => state.dtcReducer
  );

  const { data: driverBehaviorData, isLoading: isLoadingDriverBehavior } =
    useAppSelector((state) => state.driverBehaviorReducer);

  const { data: fuelData, isLoading: isLoadingFuelData } = useAppSelector(
    (state) => state.planningAidReducer
  );

  const productionData = data.allFleet.summary;
  const productionChartData = data.allFleet.assets;
  const isLoadingProduction =
    loadingStates.isLoadingFleetAssets ||
    loadingStates.isLoadingFleetDetails ||
    loadingStates.isLoadingFleetSummary;

  const utilizationData = utilizationReducer.data.allFleet.summary;
  const utilizationChartData = utilizationReducer.data.allFleet.assets;
  const isLoadingUtilization =
    utilizationReducer.loadingStates.isLoadingFleetSummary ||
    utilizationReducer.loadingStates.isLoadingFleetAssets;

  useEffect(() => {
    const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
      'Custom',
      dayjs().subtract(1, 'day'),
      dayjs(),
      timezoneBasedOnDisplayMode
    );

    void squaresHandler.getWorkOrdersWithDowntimeAggregated(
      prevDate,
      currentDate,
      true
    );

    if (!startUtcISO || !endUtcISO) {
      return;
    }

    void dtcHandler.getAllGroupedData({
      startDate: startUtcISO,
      endDate: endUtcISO,
    });

    void driverBehaviorHandler.getAllWithPastComparison({
      startDate: startUtcISO,
      endDate: endUtcISO,
    });

    const startDateFormatted = dayjs(startUtcISO).format('YYYY-MM-DD');
    const endDateFormatted = dayjs(endUtcISO).format('YYYY-MM-DD');
    void fuelAnalyticsHandler.getAssetsFuelConsumptionWithinDateRange(
      startDateFormatted,
      endDateFormatted
    );
  }, []);

  const suggestions = generateAssetRecommendations(data.allFleet.suggestions);

  return (
    <Box
      sx={{
        marginTop: '20px',
        marginBottom: '20px',
      }}
    >
      {isLoading ||
      isLoadingProduction ||
      isLoadingUtilization ||
      isLoadingDriverBehavior ||
      isLoadingFuelData ||
      isLoadingDtc ? (
        <SingleFadeLoader />
      ) : (
        <>
          <ProductionWidget
            enableDarkTheme={enableDarkTheme}
            data={productionData}
            chartData={productionChartData}
            customerCode={customerCode}
            unitSystem={unitSystem}
            suggestions={suggestions}
          />
          <UtilizationWidget
            enableDarkTheme={enableDarkTheme}
            customerCode={customerCode}
            data={utilizationData}
            chartData={utilizationChartData}
            unitSystem={unitSystem}
            downTimeData={downTimeData}
            suggestions={suggestions}
          />
          <MachineHealthWidget
            enableDarkTheme={enableDarkTheme}
            customerCode={customerCode}
            unitSystem={unitSystem}
            data={allGroupedDTCData}
            tpmsNotifications={notifications}
            assets={assets}
          />
          <OperatorBehavior
            enableDarkTheme={enableDarkTheme}
            overspeed={driverBehaviorData.overspeed}
            underspeed={driverBehaviorData.underspeed}
            data={fuelData}
          />
        </>
      )}
    </Box>
  );
};

export default PlanningAid;
